// Global Instructions Rule Applied!
//
// A fund picker that defaults to the funds you can still choose, with a way to
// see the rest.
//
// WHY THIS IS A COMPONENT AND NOT A CONVENTION. It wires `pin` from its own
// `value` internally, so a call site physically cannot forget to do it. That
// one line is the difference between "the picker is tidier" and "opening a
// capital call from 2022 silently blanks its fund on save" — the fund is
// cancelled, the gate hides it, the Select finds no matching option and
// renders empty, and the next save writes NULL.
//
// Two scopes (owner, 2026-09-04):
//   scope='fundraising' — the fundraising clock is open. Prospect forms, bulk
//       add, the fundraising kanban: pickers that choose a fund to raise into.
//   scope='any' (default) — not terminal on either clock. Commitments, capital
//       calls, transactions, journal entries: you still book against a fund
//       whose raise closed years ago.
//
// The footer only appears when something is actually hidden, so a firm with no
// closed funds never sees it.

import React from 'react'
import { Divider } from 'antd'
import Select from './Select.jsx'
import { useFundOptions } from '../../app_modules/global_configs/hooks/useFundOptions.ts'
import type { FundOptionsGate } from '../../app_modules/global_configs/fundOptions.ts'

export interface FundSelectProps extends Omit<FundOptionsGate, 'pin'> {
  /** Optional so the component can be dropped inside an antd `Form.Item`,
   *  which injects `value` and `onChange` onto its child by cloneElement. */
  value?: string | string[] | null
  onChange?: (value: any) => void
  mode?: 'multiple'
  size?: 'small' | 'middle' | 'large'
  placeholder?: string
  /** Validation state, forwarded to the underlying Select. */
  status?: 'error' | 'warning'
  disabled?: boolean
  allowClear?: boolean
  showSearch?: boolean
  style?: React.CSSProperties
  className?: string
  'data-testid'?: string
  /** Extra ids to keep visible beyond `value` — e.g. every fund already on a
   *  commitments grid the user is editing. */
  alsoPin?: string[]
}

export const FundSelect: React.FC<FundSelectProps> = ({
  value,
  onChange,
  mode,
  size = 'middle',
  placeholder = 'Select a fund',
  disabled,
  allowClear,
  style,
  className,
  alsoPin,
  scope,
  includeAll,
  onlyManagedByUs,
  ...rest
}) => {
  // The pin comes from `value` here, not from the call site. See the header.
  const pin = [
    ...(Array.isArray(value) ? value : value ? [value] : []),
    ...(alsoPin || [])
  ].filter(Boolean) as string[]

  const { options, showAll, setShowAll, hiddenCount } = useFundOptions({
    scope,
    includeAll,
    onlyManagedByUs,
    pin
  })

  return (
    <Select
      {...rest}
      mode={mode}
      size={size}
      value={value ?? undefined}
      onChange={onChange}
      placeholder={placeholder}
      disabled={disabled}
      allowClear={allowClear}
      style={style}
      className={className}
      optionFilterProp='label'
      options={options.map((o) => ({ value: o.value, label: o.label }))}
      popupRender={(menu: React.ReactNode) => (
        <>
          {menu}
          {hiddenCount > 0 ? (
            <>
              <Divider style={{ margin: '4px 0' }} />
              <div className='px-2 pb-1'>
                <button
                  type='button'
                  data-testid='fund-select-show-all'
                  className='cursor-pointer text-blue-600 hover:text-blue-700 hover:underline bg-transparent border-0 p-0 text-left text-xs'
                  // onMouseDown + preventDefault: a plain onClick closes the
                  // dropdown before the state lands, so the list never reopens
                  // with the extra funds in it.
                  onMouseDown={(e) => {
                    e.preventDefault()
                    setShowAll(!showAll)
                  }}
                >
                  {showAll
                    ? 'Show active funds only'
                    : `Show all funds (${hiddenCount} closed)`}
                </button>
              </div>
            </>
          ) : null}
        </>
      )}
    />
  )
}

export default FundSelect
